import React, { useState, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../contexts/PermissionsContext';
import { useLocation } from '../../contexts/LocationContext';
import { useWarehouse } from '../../contexts/WarehouseContext';
import api from '../../services/api';

// Mapowanie ścieżki -> moduł uprawnień (null = zawsze widoczny)
const pathToModule = {
  '/pos': 'pos',
  '/products': 'produkty',
  '/customers': 'klienci',
  '/orders': 'zamowienia',
  '/sales-invoices': 'faktury',
  '/purchase-invoices': 'faktury_zakupu',
  '/reklamacje': 'reklamacje',
  '/coupons': 'kupony',
  '/warehouse': 'magazyn',
  '/kolektor': 'kolektor',
  '/location-pricing': 'cenowki',
  '/kasa-bank': 'kasa_bank',
  '/accounting': 'ksiegowosc',
  '/shipping': 'wysylka',
  '/tasks': null,      // Zadania — zawsze
  '/wiadomosci': 'wiadomosci',
  '/reports': 'raporty',
  '/bonuses': 'premie',
  '/grafik': null,     // Moj grafik — zawsze widoczny dla zalogowanego usera
  '/admin': null,      // Admin — osobna logika (isAdmin)
};

const navSections = [
  {
    title: 'Glowne',
    items: [
      { label: 'Kasa POS', icon: 'fa-cash-register', path: '/pos' },
      { label: 'Produkty', icon: 'fa-box-open', path: '/products' },
      { label: 'Klienci', icon: 'fa-users', path: '/customers' },
      { label: 'Zamowienia', icon: 'fa-shopping-cart', path: '/orders' },
      // Instrukcje dla pracownikow. Celowo w sekcji glownej, a nie na dole -
      // pomoc, ktorej nikt nie znajduje, nie jest pomoca.
      { label: 'Jak korzystac', icon: 'fa-circle-question', path: '/pomoc' },
    ],
  },
  {
    title: 'Dokumenty',
    items: [
      { label: 'Faktury sprzedazy', icon: 'fa-file-invoice-dollar', path: '/sales-invoices' },
      { label: 'Faktury zakupowe', icon: 'fa-file-invoice', path: '/purchase-invoices' },
      { label: 'Reklamacje', icon: 'fa-exclamation-triangle', path: '/reklamacje' },
      { label: 'Kupony', icon: 'fa-ticket-alt', path: '/coupons' },
    ],
  },
  {
    title: 'Magazyn / Finanse',
    items: [
      { label: 'Magazyn', icon: 'fa-warehouse', path: '/warehouse' },
      { label: 'Kolektor', icon: 'fa-barcode', path: '/kolektor' },
      { label: 'Ceny / Cenowki', icon: 'fa-tags', path: '/location-pricing' },
      { label: 'Kasa / Bank', icon: 'fa-money-bill-wave', path: '/kasa-bank' },
      { label: 'Ksiegowosc', icon: 'fa-book', path: '/accounting' },
      { label: 'Moja premia', icon: 'fa-trophy', path: '/bonuses/my' },
      { label: 'Moj grafik', icon: 'fa-calendar-week', path: '/grafik' },
    ],
  },
  {
    title: 'Komunikacja',
    items: [
      { label: 'Wysylka', icon: 'fa-truck', path: '/shipping' },
      { label: 'Zadania', icon: 'fa-tasks', path: '/tasks' },
      { label: 'Wiadomosci', icon: 'fa-envelope', path: '/wiadomosci', badge: true },
    ],
  },
  {
    title: 'System',
    items: [
      { label: 'Raporty', icon: 'fa-chart-line', path: '/reports' },
      { label: 'Admin', icon: 'fa-shield-alt', path: '/admin', danger: true },
    ],
  },
];

function getInitials(login) {
  if (!login) return '??';
  return login.slice(0, 2).toUpperCase();
}

export default function Sidebar({ onToggleTheme, isDark, mobileOpen }) {
  const { user, isAdmin } = useAuth();
  const { hasPermission, loading: permLoading } = usePermissions();
  const login = user?.login || 'User';
  const role = isAdmin ? 'Administrator' : 'Kasjer';
  const initials = getInitials(login);

  // Licznik niezakonczonych zamowien (badge przy "Zamowienia") — dla BIEZACEJ lokalizacji,
  // spojnie z lista zamowien; odswiezany co 60s i przy zmianie lokalizacji.
  const { locationId } = useLocation();
  const { warehouseId } = useWarehouse();
  const currentLocationId = locationId || warehouseId;
  const [ordersCount, setOrdersCount] = useState(0);
  useEffect(() => {
    let mounted = true;
    const fetchCount = () => {
      const cfg = currentLocationId ? { params: { location_id: currentLocationId } } : undefined;
      api.get('/orders/unfinished-count', cfg)
        .then((res) => { if (mounted) setOrdersCount(Number(res?.data?.data?.count) || 0); })
        .catch(() => { /* brak dostepu / blad -> brak badge */ });
    };
    fetchCount();
    const id = setInterval(fetchCount, 60000);
    return () => { mounted = false; clearInterval(id); };
  }, [currentLocationId]);

  return (
    <aside className={`sidebar${mobileOpen ? ' mobile-open' : ''}`}>
      <div className="sidebar-header">
        <div className="sidebar-logo">
          V3<span className="logo-full">SYS</span>
        </div>
      </div>

      <nav className="sidebar-nav">
        {navSections.map((section, idx) => {
          // Filtruj elementy sekcji wg uprawnień
          const visibleItems = section.items.filter((item) => {
            // Admin — tylko dla adminów
            if (item.path === '/admin') return isAdmin;
            // Moduł bez mapowania lub admin — zawsze widoczny
            const mod = pathToModule[item.path];
            if (!mod || isAdmin) return true;
            // Podczas ładowania uprawnień — pokaż wszystko (unikaj migania)
            if (permLoading) return true;
            // Sprawdź uprawnienie dostep do modułu
            return hasPermission(mod, 'dostep');
          });
          // Ukryj pustą sekcję
          if (visibleItems.length === 0) return null;
          return (
            <React.Fragment key={section.title}>
              {idx > 0 && <div className="nav-divider" />}
              <div className="nav-section-title">{section.title}</div>
              {visibleItems.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  end={item.path === '/'}
                  className={({ isActive }) =>
                    `nav-item${isActive ? ' active' : ''}${item.danger ? ' danger' : ''}`
                  }
                >
                  <i className={`fas ${item.icon}`} />
                  <span className="nav-label">{item.label}</span>
                  {item.badge && <span className="nav-badge">!</span>}
                  {item.path === '/orders' && ordersCount > 0 && (
                    <span className="nav-badge" title="Niezakończone zamówienia">{ordersCount}</span>
                  )}
                </NavLink>
              ))}
            </React.Fragment>
          );
        })}
      </nav>

      <div className="sidebar-footer">
        {onToggleTheme && (
          <button
            className="nav-item"
            onClick={onToggleTheme}
            type="button"
          >
            <i className={`fas ${isDark ? 'fa-sun' : 'fa-moon'}`} />
            <span className="nav-label">{isDark ? 'Jasny motyw' : 'Ciemny motyw'}</span>
          </button>
        )}
        <div className="sidebar-user">
          <div className="user-avatar">{initials}</div>
          <div className="user-info">
            <div className="user-name">{login}</div>
            <div className="user-role">{role}</div>
          </div>
        </div>
      </div>
    </aside>
  );
}
